<div class="modal" [class.modal-open]="visible">
  <div class="modal-box max-w-2xl">
    <h3 class="font-bold text-lg mb-4">Book Seat</h3>

    <!-- Enhanced Trip Context Section -->
    @if (selectedBus) {
      <div class="bg-base-200 rounded-lg p-4 mb-4">
        <div class="flex items-center justify-between mb-3">
          <div>
            <h3 class="font-semibold">{{ selectedBus.busAssetName }}</h3>
            <p class="text-sm text-gray-600">{{ selectedBus.routeName }}</p>
          </div>
          <div class="text-right">
            <div class="text-xl font-bold text-primary">
              {{ selectedBus.estimatedArrivalMinutes }} min
            </div>
            @if (selectedBus.operationProgress?.scheduledArrivalTime) {
              <div class="text-xs text-gray-500">
                ETA: {{ selectedBus.operationProgress.scheduledArrivalTime | date:'shortTime' }}
              </div>
            }
          </div>
        </div>

        <!-- Progress Info -->
        @if (selectedBus.operationProgress) {
          <div class="flex items-center gap-2 mb-3">
            <div class="badge badge-info badge-sm">
              {{ selectedBus.operationProgress.progressPercentage }}% complete
            </div>
            @if (selectedBus.operationProgress.isDelayed) {
              <div class="badge badge-warning badge-sm">
                Delayed {{ selectedBus.operationProgress.delayMinutes }} min
              </div>
            }
          </div>
        }

        <!-- Seat Forecast -->
        @if (selectedBus.seatForecast) {
          <div class="grid grid-cols-3 gap-3 text-center">
            <div>
              <div class="text-xs text-gray-600">Now</div>
              <div class="text-lg font-bold">
                {{ selectedBus.seatForecast.currentAvailable }}
              </div>
            </div>
            <div>
              <div class="text-xs text-gray-600">At Arrival</div>
              <div class="text-lg font-bold text-success">
                {{ selectedBus.seatForecast.forecastAvailable }}
              </div>
            </div>
            <div>
              <div class="text-xs text-gray-600">Total</div>
              <div class="text-lg font-bold text-gray-500">
                {{ selectedBus.totalSeats }}
              </div>
            </div>
          </div>

          @if (selectedBus.seatForecast && selectedBus.seatForecast.seatsBecomingAvailable > 0) {
            <div class="mt-3 text-sm">
              <span class="text-success font-medium">
                +{{ selectedBus.seatForecast.seatsBecomingAvailable }} seats
              </span>
              <span class="text-gray-600">opening up before arrival</span>
            </div>
          }
        }
      </div>
    }

    <app-error-display
      *ngIf="showError()"
      [error]="errorDetails"
      [showRetry]="true"
      (retry)="onRetry()"
      (dismiss)="onDismissError()"
    >
    </app-error-display>

    <form [formGroup]="bookingForm" (ngSubmit)="onSubmit()" class="space-y-4">
      <!-- Seat Selection -->
      <div class="form-control">
        <label class="label">
          <span class="label-text">Select Seat</span>
        </label>
        <select formControlName="seatId" class="select select-bordered">
          <option value="">Select a seat</option>
          @for (seat of availableSeats(); track seat.id) {
            <option [value]="seat.id">{{ seat.nickName | seatPickerLabel: seat.seatNumber }}</option>
          }
        </select>
      </div>

      <!-- Destination -->
      <div class="form-control">
        <label class="label">
          <span class="label-text">Destination</span>
        </label>
        <select
          formControlName="destinationTerminalId"
          class="select select-bordered"
          (change)="onDestinationChange()"
        >
          <option value="">Select destination</option>
          @for (dest of selectedBus?.possibleDestinations; track dest.terminalId) {
            <option [value]="dest.terminalId">
              {{ dest.terminalName }} - {{ dest.fare | currency:'UGX':'symbol':'1.0-0' }}
            </option>
          }
        </select>
      </div>

      <!-- Passenger Info -->
      <div class="grid grid-cols-2 gap-4">
        <div class="form-control">
          <label class="label">
            <span class="label-text">Passenger Name</span>
          </label>
          <input
            type="text"
            formControlName="customerName"
            class="input input-bordered"
            placeholder="Full name"
          />
        </div>
        <div class="form-control">
          <label class="label">
            <span class="label-text">Phone Number</span>
          </label>
          <input
            type="tel"
            formControlName="customerPhone"
            class="input input-bordered"
            placeholder="+233..."
          />
        </div>
      </div>

      <!-- Payment Method -->
      <div class="form-control">
        <label class="label">
          <span class="label-text">Payment Method</span>
        </label>
        <div class="flex gap-4">
          <label class="label cursor-pointer">
            <input
              type="radio"
              formControlName="paymentMethod"
              value="CASH"
              class="radio radio-primary"
            />
            <span class="label-text ml-2">Cash</span>
          </label>
          <label class="label cursor-pointer">
            <input
              type="radio"
              formControlName="paymentMethod"
              value="MOBILE_MONEY"
              class="radio radio-primary"
            />
            <span class="label-text ml-2">Mobile Money</span>
          </label>
          <label class="label cursor-pointer">
            <input
              type="radio"
              formControlName="paymentMethod"
              value="CARD"
              class="radio radio-primary"
            />
            <span class="label-text ml-2">Card</span>
          </label>
        </div>
      </div>

      <!-- Fare Display -->
      <div class="bg-gray-50 p-4 rounded-lg">
        <div class="flex justify-between items-center">
          <span class="text-lg font-semibold">Total Fare:</span>
          <span class="text-2xl font-bold text-primary">
            {{ selectedFare() | currency:'UGX':'symbol':'1.0-0' }}
          </span>
        </div>
      </div>

      <!-- Actions -->
      <div class="modal-action">
        <button type="button" class="btn" (click)="close()">Cancel</button>
        <button
          type="submit"
          class="btn btn-primary"
          [disabled]="!bookingForm.valid || bookSeatMutation.isPending()"
        >
          @if (bookSeatMutation.isPending()) {
            <span class="loading loading-spinner loading-sm"></span>
            Processing...
          } @else {
            Confirm Booking
          }
        </button>
      </div>
    </form>
  </div>
</div>

